/** Terra Search Widget — Entry point.
 *
 * Two mount modes:
 *   1. Normal pages: Shadow DOM in header for CSS isolation (search bar + dropdown only)
 *   2. catalogsearch page: ALSO mounts in .column.main for the full results page
 *      (light DOM, not Shadow DOM, so it flows naturally in Magento's layout)
 */

import { render, h } from 'preact';
import { getConfig } from './config';
import { SearchApp } from './components/SearchApp';
import widgetCSS from './styles';

function disableKlevu() {
  const klevuSelectors = [
    '.klevu-search-field',
    '.klevu-search-results',
    '#klevu-search',
    '.klevuSearchResults',
    '.klevu_landing',
    '[data-klevu-search]',
  ];

  for (const sel of klevuSelectors) {
    document.querySelectorAll(sel).forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });
  }

  document.querySelectorAll('link[href*="klevu"], style[data-klevu]').forEach((el) => {
    el.remove();
  });
}

function mount() {
  const config = getConfig();
  const root = document.querySelector(config.mountSelector);

  if (!root) {
    console.error(`[TerraSearch] Mount target "${config.mountSelector}" not found.`);
    return;
  }

  if (!config.apiUrl) {
    console.error('[TerraSearch] apiUrl not configured. Widget will not load.');
    return;
  }

  disableKlevu();

  // --- Mount search bar in header (Shadow DOM for CSS isolation) ---
  let container: HTMLElement;
  const existingShadow = (root as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
  if (existingShadow) {
    // Re-use existing shadow root (snippet pasted twice, or script loaded twice)
    container = existingShadow.getElementById('terra-search-container') as HTMLElement;
    if (!container) {
      container = document.createElement('div');
      container.id = 'terra-search-container';
      existingShadow.appendChild(container);
    }
  } else if (typeof root.attachShadow === 'function') {
    try {
      const shadow = root.attachShadow({ mode: 'open' });
      const styleEl = document.createElement('style');
      styleEl.textContent = widgetCSS;
      shadow.appendChild(styleEl);
      container = document.createElement('div');
      container.id = 'terra-search-container';
      shadow.appendChild(container);
    } catch {
      // Fallback if attachShadow fails (e.g. Clarity.js conflict)
      const styleEl = document.createElement('style');
      styleEl.textContent = widgetCSS;
      root.appendChild(styleEl);
      container = document.createElement('div');
      container.id = 'terra-search-container';
      root.appendChild(container);
    }
  } else {
    const styleEl = document.createElement('style');
    styleEl.textContent = widgetCSS;
    root.appendChild(styleEl);
    container = document.createElement('div');
    container.id = 'terra-search-container';
    root.appendChild(container);
  }

  // --- On catalogsearch page: also mount results in .column.main ---
  const cl = document.body.classList;
  const isResultsPage = cl.contains('catalogsearch-result-index') || cl.contains('search-index-index');
  if (isResultsPage) {
    // Find or create results mount point in Magento's content area.
    // Uses Shadow DOM to isolate from Magento's CSS cascade.
    const columnMain = document.querySelector('.column.main');
    if (columnMain) {
      // Hide any remaining native content
      for (let i = 0; i < columnMain.children.length; i++) {
        (columnMain.children[i] as HTMLElement).style.display = 'none';
      }

      // Create Shadow DOM results mount
      const resultsHost = document.createElement('div');
      resultsHost.id = 'terra-search-results-root';
      columnMain.prepend(resultsHost);

      let resultsContainer: HTMLElement;
      try {
        const shadow = resultsHost.attachShadow({ mode: 'open' });
        const styleEl = document.createElement('style');
        styleEl.textContent = widgetCSS;
        shadow.appendChild(styleEl);
        resultsContainer = document.createElement('div');
        resultsContainer.id = 'terra-search-results-container';
        shadow.appendChild(resultsContainer);
      } catch {
        // Fallback to light DOM
        const styleEl = document.createElement('style');
        styleEl.textContent = widgetCSS;
        resultsHost.appendChild(styleEl);
        resultsContainer = document.createElement('div');
        resultsContainer.id = 'terra-search-results-container';
        resultsHost.appendChild(resultsContainer);
      }

      render(h(SearchApp, { resultsMode: true }), resultsContainer);
    }
  }

  // Render search bar app (on all pages including catalogsearch)
  render(h(SearchApp, null), container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}

export { mount };
